import { Anchor, AppShell, Badge, Burger, Group, Indicator, Menu, NavLink, Stack, Text, UnstyledButton } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import type { ReactNode } from "react";
import { NavLink as RouterLink, useLocation } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { get, type Project } from "../lib/api";
import { useAuth } from "../lib/auth";
import { MODULES, modulePath, moduleOfProduct, type ModuleSlug } from "../lib/modules";
import { useMe } from "../lib/hooks";

/** Where users write for help (the same address as the desktop apps' About boxes; server: PRAYOG_SUPPORT_EMAIL). */
export const SUPPORT_EMAIL = "support@prayog-qp.in";
export const HUB_URL = "https://qmdhub.co.in/";
export const NQM_URL = "https://dst.gov.in/";
/** The About / landing page: a static page (web/public/about.html, edited by hand). Visitors who are not logged in get
 *  it at "/"; "?from=app" turns its "Log in" buttons into "Open PRAYOG-QP". */
export const ABOUT_PAGE = "/about.html";

/** The About text of every PRAYOG-QP program (desktop: "About" box, prayog_launcher.show_prayog_about), with its links. */
export function AboutText() {
  const link = (href: string, text: string) => (
    <Anchor href={href} target="_blank" rel="noopener noreferrer" size="xs" inherit>{text}</Anchor>
  );
  return (
    <Stack gap={6} mt="auto" pt="md" data-testid="about-text">
      <Text size="xs" c="dimmed" lh={1.45}>
        This product has been developed at the {link(HUB_URL, "Quantum Materials and Devices Hub")}, established under
        the {link(NQM_URL, "National Quantum Mission")}, Department of Science and Technology, Govt of India.
      </Text>
      <Text size="xs" c="dimmed" lh={1.45}>
        Contact <Anchor href={`mailto:${SUPPORT_EMAIL}`} size="xs" inherit>{SUPPORT_EMAIL}</Anchor> for any queries / support.
      </Text>
      <Anchor href={`${ABOUT_PAGE}?from=app`} size="xs" fw={600} data-testid="about-link">
        About PRAYOG-QP
      </Anchor>
    </Stack>
  );
}

type NavItem = { to: string; label: string; color?: string; count?: number };

/** The left menu: Home, one entry per module (its home page, with the user's project count), the user's work, help. */
export function navSections(projects: Project[] | undefined, admin: boolean): { title?: string; items: NavItem[] }[] {
  const count = (product: string | null) => (product && projects ? projects.filter((p) => p.product === product).length : 0);
  return [
    { items: [{ to: "/", label: "Home" }] },
    { title: "Modules", items: MODULES.map((m) => ({ to: modulePath(m.slug), label: m.name, color: m.color,
      count: count(m.product) || undefined })) },
    { title: "My work", items: [{ to: "/projects", label: "All projects", count: projects?.length || undefined },
      { to: "/jobs", label: "Jobs" }] },
    { title: "Help", items: [{ to: "/references", label: "References" }, { to: "/account", label: "My plan & usage" },
      ...(admin ? [{ to: "/admin", label: "Admin" }] : [])] },
  ];
}

export function Layout({ children }: { children: ReactNode }) {
  const [opened, { toggle, close }] = useDisclosure();
  const { data: me } = useMe();
  const { logout, accountUrl } = useAuth();
  const { pathname } = useLocation();
  const projects = useQuery({ queryKey: ["projects"], queryFn: () => get<Project[]>("/projects"), enabled: !!me });
  const sections = navSections(projects.data, me?.role === "admin");
  const here = moduleOfPath(pathname, projects.data);
  // a project's page lights up its module, not "All projects"
  const active = (to: string) => (to === "/" ? pathname === "/" : to === "/projects" ? pathname === to && !here
    : pathname.startsWith(to) || (!!here && to === modulePath(here)));

  return (
    <AppShell header={{ height: 60 }} navbar={{ width: 220, breakpoint: "sm", collapsed: { mobile: !opened } }} padding="md">
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between" wrap="nowrap">
          <Group gap="sm" wrap="nowrap">
            <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" aria-label="Menu" />
            <img src="/logo.png" alt="" height={34} style={{ display: "block" }} />
            <Text fw={700} size="lg">
              PRAYOG-QP
            </Text>
          </Group>
          {me && (
            <Group gap="xs" wrap="nowrap">
              <Badge variant="light" visibleFrom="xs">
                {me.plan_name} plan
              </Badge>
              <Menu position="bottom-end" withinPortal>
                <Menu.Target>
                  <UnstyledButton aria-label="Account menu">
                    <Indicator disabled={!me.unread_notifications} label={me.unread_notifications} size={16}>
                      <Text size="sm" fw={500} maw={180} truncate="end">
                        {me.name || me.email}
                      </Text>
                    </Indicator>
                  </UnstyledButton>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Label>{me.email}</Menu.Label>
                  <Menu.Item component={RouterLink} to="/account">
                    My plan & usage
                  </Menu.Item>
                  <Menu.Item component={RouterLink} to="/account#notices">
                    Notices{me.unread_notifications ? ` (${me.unread_notifications} new)` : ""}
                  </Menu.Item>
                  {accountUrl && (
                    <Menu.Item component="a" href={accountUrl}>
                      Password and sign-in
                    </Menu.Item>
                  )}
                  <Menu.Divider />
                  <Menu.Item onClick={logout}>Log out</Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </Group>
          )}
        </Group>
      </AppShell.Header>
      <AppShell.Navbar p="sm">
        <Stack gap={2} style={{ overflowY: "auto" }}>
          {sections.map((sec, i) => (
            <Stack gap={2} key={sec.title ?? i} data-testid={`nav-${(sec.title ?? "home").toLowerCase().replace(" ", "-")}`}>
              {sec.title && <Text size="xs" fw={700} c="dimmed" tt="uppercase" mt="sm" mb={2} px="sm" style={{ letterSpacing: "0.06em" }}>{sec.title}</Text>}
              {sec.items.map((it) => (
                <NavLink key={it.to} component={RouterLink} to={it.to} label={it.label} active={active(it.to)} onClick={close}
                  leftSection={it.color ? <span style={{ width: 9, height: 9, borderRadius: 3, background: it.color, display: "block" }} /> : undefined}
                  rightSection={it.count ? <Text size="xs" c="dimmed">{it.count}</Text> : undefined} />
              ))}
            </Stack>
          ))}
        </Stack>
        <AboutText />
      </AppShell.Navbar>
      <AppShell.Main>
        {me?.status === "pending" && (
          <Text c="orange" mb="md" fw={500}>
            Your account is waiting for approval by an administrator. You can look around; calculations start once it is approved.
          </Text>
        )}
        {children}
      </AppShell.Main>
    </AppShell>
  );
}

/** The module a page belongs to: /m/<slug>/..., or a project page (by the project's product). */
function moduleOfPath(pathname: string, projects: Project[] | undefined): ModuleSlug | undefined {
  const m = /^\/m\/([^/]+)/.exec(pathname);
  if (m) return m[1] as ModuleSlug;
  const p = /^\/projects\/([^/]+)/.exec(pathname);
  if (p) return moduleOfProduct(projects?.find((x) => x.id === p[1])?.product)?.slug;
  if (pathname === "/qtetris") return "qtetris";
  return undefined;
}

export function PageTitle({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <Group justify="space-between" mb="md" wrap="wrap">
      <Text component="h1" fw={700} fz={24} m={0}>
        {title}
      </Text>
      <Group gap="xs">{children}</Group>
    </Group>
  );
}

